import { useState } from 'react';
import { MISSION_PLATFORMS, type Mission, type PostulerInput } from '@/lib/missions';

export interface SocialProfile {
  lienfacebook?: string | null;
  lieninstagram?: string | null;
  lientiktok?: string | null;
  lientwitter?: string | null;
  lienyoutube?: string | null;
}

interface Props {
  mission: Mission;
  profile: SocialProfile;
  onClose: () => void;
  onSubmit: (input: PostulerInput) => Promise<void>;
}

const PROFILE_KEYS: Record<string, keyof SocialProfile> = {
  facebook: 'lienfacebook',
  instagram: 'lieninstagram',
  tiktok: 'lientiktok',
  x: 'lientwitter',
  youtube: 'lienyoutube',
};

/** Domaines attendus par réseau, pour vérifier le lien collé par le candidat. */
const PLATFORM_DOMAINS: Record<string, string[]> = {
  tiktok: ['tiktok.com'],
  instagram: ['instagram.com'],
  facebook: ['facebook.com', 'fb.com'],
  youtube: ['youtube.com', 'youtu.be'],
  x: ['x.com', 'twitter.com'],
};

/** Le lien est-il un vrai lien de profil du réseau concerné ? */
export function isValidSocialLink(platform: string, value: string): boolean {
  const raw = value.trim().toLowerCase();
  if (!raw) return false;
  const withProtocol = /^https?:\/\//.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(withProtocol);
    const domains = PLATFORM_DOMAINS[platform] || [];
    return domains.some((d) => url.hostname === d || url.hostname.endsWith(`.${d}`));
  } catch {
    return false;
  }
}

/** Candidature : liens réseaux sociaux + nombre d'abonnés par réseau. */
export default function PostulerModal({ mission, profile, onClose, onSubmit }: Props) {
  const plateformes = (mission.plateformes || []).filter(Boolean);
  const liste = (plateformes.length > 0 ? MISSION_PLATFORMS.filter((p) => plateformes.includes(p.key)) : MISSION_PLATFORMS);

  const [liens, setLiens] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    liste.forEach((p) => {
      const key = PROFILE_KEYS[p.key];
      initial[p.key] = key ? (profile[key] || '') : '';
    });
    return initial;
  });
  const [abonnes, setAbonnes] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    liste.forEach((p) => { initial[p.key] = ''; });
    return initial;
  });
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const total = Object.values(abonnes).reduce((sum, v) => sum + (Number(v) || 0), 0);

  const submit = async () => {
    const hasLien = liste.some((p) => (liens[p.key] || '').trim());
    if (!hasLien) {
      setError('Renseignez au moins un lien de profil sur un réseau social.');
      return;
    }
    const invalide = liste.find((p) => {
      const value = (liens[p.key] || '').trim();
      return value.length > 0 && !isValidSocialLink(p.key, value);
    });
    if (invalide) {
      setError(`Le lien ${invalide.label} n’est pas valide : collez l’adresse complète de votre profil (ex. https://www.${(PLATFORM_DOMAINS[invalide.key] || ['exemple.com'])[0]}/votrecompte).`);
      return;
    }
    if (!liste.some((p) => isValidSocialLink(p.key, liens[p.key] || ''))) {
      setError('Aucun lien valide détecté. Vérifiez l’adresse de votre profil.');
      return;
    }
    if (total <= 0) {
      setError('Indiquez votre nombre d’abonnés sur au moins un réseau.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const abonnesNum: Record<string, number> = {};
      liste.forEach((p) => { abonnesNum[p.key] = Number(abonnes[p.key]) || 0; });
      await onSubmit({
        liens: Object.fromEntries(Object.entries(liens).map(([k, v]) => [k, v.trim()])),
        abonnes: abonnesNum,
        message,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible d’envoyer la candidature.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-background-50 rounded-xl border border-background-200/70">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-200/70">
          <div className="min-w-0">
            <h3 className="text-base font-bold font-heading text-foreground-950 truncate">{mission.titre}</h3>
            <p className="text-xs text-foreground-500 mt-0.5">Postulez avec vos réseaux sociaux et votre audience.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-accent-50 border border-accent-200 rounded-lg">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold text-accent-900 uppercase tracking-wide">Tarif de la mission</p>
              <p className="text-sm font-bold text-foreground-950">
                {(Number(mission.tarif_1000_vues) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {mission.monaie || 'MAD'}
                <span className="text-[10px] font-medium text-foreground-500"> / 1000 vues</span>
              </p>
            </div>
            {mission.musique_autorisee && (
              <span className="px-2 py-1 rounded-full bg-background-50 text-accent-900 text-[10px] font-semibold whitespace-nowrap">
                <i className="ri-music-2-line mr-1"></i>Musique OK
              </span>
            )}
          </div>

          {liste.map((p) => (
            <div key={p.key} className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
              <div className="sm:col-span-2 relative">
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                  <i className={`${p.icon} mr-1`}></i>{p.label} — lien du profil
                </label>
                <input
                  type="text"
                  value={liens[p.key] || ''}
                  onChange={(e) => setLiens((prev) => ({ ...prev, [p.key]: e.target.value }))}
                  placeholder="https://…"
                  className={`w-full pl-3 pr-9 py-2.5 bg-background-100 border rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none transition-colors ${
                    (liens[p.key] || '').trim()
                      ? isValidSocialLink(p.key, liens[p.key])
                        ? 'border-accent-300 focus:border-accent-400'
                        : 'border-red-300 focus:border-red-400'
                      : 'border-background-200/70 focus:border-primary-300'
                  }`}
                />
                {(liens[p.key] || '').trim() && (
                  <i
                    className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm ${isValidSocialLink(p.key, liens[p.key]) ? 'ri-check-line text-accent-600' : 'ri-error-warning-line text-red-500'}`}
                    title={isValidSocialLink(p.key, liens[p.key]) ? 'Lien valide' : 'Lien invalide pour ce réseau'}
                  ></i>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Abonnés</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={abonnes[p.key] || ''}
                  onChange={(e) => setAbonnes((prev) => ({ ...prev, [p.key]: e.target.value.replace(/\D/g, '') }))}
                  placeholder="0"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>
          ))}

          <p className="text-[11px] text-foreground-500 bg-background-100 rounded-md px-3 py-2 flex items-start gap-1.5">
            <i className="ri-information-line mt-0.5"></i>
            Total déclaré : <strong className="text-foreground-800">{total.toLocaleString('fr-FR')} abonnés</strong>.
            Les liens déjà enregistrés dans votre profil sont pré-remplis automatiquement.
          </p>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Message au propriétaire (facultatif)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 300))}
              rows={3}
              placeholder="Votre expérience, vos formats, vos délais…"
              className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
            />
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-1.5">
              <i className="ri-error-warning-line mt-0.5"></i>
              {error}
            </p>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-background-200/70">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors">
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
            Postuler
          </button>
        </div>
      </div>
    </div>
  );
}